import { useEffect, useState, useCallback } from 'react';
import { useSearchParams, useNavigate } from 'react-router-dom';
import { Box, CircularProgress, Typography, Alert, Dialog, DialogTitle, DialogContent, DialogContentText, DialogActions, Button } from '@mui/material';
import {
  MedicalServices as MedNecIcon,
  ArrowBack as BackIcon,
  Save as SaveIcon,
  Undo as UndoIcon,
  Send as SubmitIcon,
  ContactMail as PhysicianIcon,
  ThumbUp as ApprovedIcon,
  ThumbDown as DeniedIcon,
  Replay as ReturnIcon,
  Cancel as CancelIcon,
} from '@mui/icons-material';
import { useAppDispatch, useAppSelector } from '../../../../app/hooks';
import {
  useGetCaseByIdQuery,
  useGetGapAnalysisQuery,
  useTransitionCaseMutation,
  clearCase,
} from '../../../../services/MedNecService';
import { PageContainer, PageCard, DashboardHeader, type DashboardHeaderAction } from '../../../common/components';
import {
  PatientSection,
  PhysicianSection,
} from '../sections';
import CaseFormTabs from '../CaseFormTabs';
import { useCaseActions } from '../../hooks/useCaseActions';

const Label: React.FC<{ children: React.ReactNode }> = ({ children }) => (
  <Typography variant="caption" sx={{ fontWeight: 600, color: '#475569', textTransform: 'uppercase', letterSpacing: '0.04em', mb: 0.5, display: 'block' }}>
    {children}
  </Typography>
);

export const MedNecCaseDetail: React.FC = () => {
  const [searchParams] = useSearchParams();
  const id = searchParams.get('id') || '';
  const navigate = useNavigate();
  const dispatch = useAppDispatch();

  const { isLoading, isError, error } = useGetCaseByIdQuery(id, {
    skip: !id,
    refetchOnMountOrArgChange: true,
  });

  const currentCase = useAppSelector(s => s.medNecCase.currentCase);

  const { data: gapStatus } = useGetGapAnalysisQuery(id, { skip: !id });
  const gapAnalysis = gapStatus?.analysis;
  const allCriteriaMet = !!(gapAnalysis && gapAnalysis.criteria.length > 0 && gapAnalysis.criteria.every(c => c.status === 'MET'));

  const { handleSave, handleDiscard, isEdited, isSaving } = useCaseActions();

  const [transitionCase, { isLoading: isTransitioning }] = useTransitionCaseMutation();
  const [confirmDialog, setConfirmDialog] = useState<{ target: string; label: string; reason: string } | null>(null);
  const [transitionError, setTransitionError] = useState<string | null>(null);

  const handleTransition = useCallback((target: string, label: string, reason: string) => {
    setTransitionError(null);
    setConfirmDialog({ target, label, reason });
  }, []);

  const confirmTransition = useCallback(async () => {
    if (!confirmDialog) return;
    try {
      await transitionCase({ caseId: id, targetStatus: confirmDialog.target, reason: confirmDialog.reason }).unwrap();
      setTransitionError(null);
    } catch (err: unknown) {
      const message = (err && typeof err === 'object' && 'data' in err)
        ? String((err as { data: unknown }).data)
        : (err instanceof Error ? err.message : 'Failed to transition case. Please try again.');
      setTransitionError(message);
    } finally {
      setConfirmDialog(null);
    }
  }, [confirmDialog, id, transitionCase]);

  useEffect(() => {
    return () => { dispatch(clearCase()); };
  }, [dispatch, id]);

  if (!id) {
    return <Alert severity="warning" sx={{ m: 2 }}>No case ID provided.</Alert>;
  }

  if (isLoading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: 300 }}>
        <CircularProgress />
      </Box>
    );
  }

  if (isError) {
    return (
      <Alert severity="error" sx={{ m: 2 }}>
        Failed to load case. {error && 'status' in error ? `Status: ${error.status}` : ''}
      </Alert>
    );
  }

  if (!currentCase) {
    return <Typography sx={{ m: 2 }}>Case not found.</Typography>;
  }

  const status = currentCase.status;
  const isTerminal = ['PAPP', 'NOAR', 'CANC'].includes(status);

  const workflowActions: DashboardHeaderAction[] = [];

  if (status === 'UREV' || status === 'AWPR') {
    if (allCriteriaMet) {
      workflowActions.push({
        label: 'Submit to Payer', icon: SubmitIcon,
        onClick: () => handleTransition('SUBT', 'Submit to Payer', 'Submitted to payer'),
        disabled: isTransitioning,
      });
    }
    if (status === 'UREV') {
      workflowActions.push({
        label: 'Awaiting Physician', icon: PhysicianIcon,
        onClick: () => handleTransition('AWPR', 'Awaiting Physician', 'Awaiting physician response'),
        variant: 'outlined', color: 'warning', disabled: isTransitioning,
      });
    }
  }

  if (status === 'SUBT') {
    workflowActions.push({
      label: 'Payer Approved', icon: ApprovedIcon,
      onClick: () => handleTransition('PAPP', 'Payer Approved', 'Payer approved authorization'),
      color: 'success', disabled: isTransitioning,
    });
    workflowActions.push({
      label: 'Payer Denied', icon: DeniedIcon,
      onClick: () => handleTransition('PDEN', 'Payer Denied', 'Payer denied authorization'),
      color: 'error', disabled: isTransitioning,
    });
  }

  if (status === 'PDEN') {
    workflowActions.push({
      label: 'Return to Review', icon: ReturnIcon,
      onClick: () => handleTransition('UREV', 'Return to Review', 'Returned to review after denial'),
      disabled: isTransitioning,
    });
  }

  if (!isTerminal) {
    workflowActions.push({
      label: 'Cancel Case', icon: CancelIcon,
      onClick: () => handleTransition('CANC', 'Cancel Case', 'Case cancelled'),
      variant: 'outlined', color: 'error', disabled: isTransitioning,
    });
  }

  const actions: DashboardHeaderAction[] = [
    { label: 'Back', icon: BackIcon, onClick: () => navigate(-1), variant: 'outlined' as const },
    ...workflowActions,
    ...(isEdited ? [
      { label: 'Discard', icon: UndoIcon, onClick: handleDiscard, variant: 'outlined' as const, disabled: isSaving },
      { label: isSaving ? 'Saving...' : 'Save', icon: SaveIcon, onClick: handleSave, disabled: isSaving },
    ] : []),
  ];

  return (
    <PageContainer>
      <Box display="flex" justifyContent="center">
        <PageCard sx={{
          background: 'linear-gradient(135deg, #f8fafc 0%, #ffffff 100%)',
          minWidth: '1000px',
          maxWidth: '1100px',
          width: '100%',
        }}>
          <DashboardHeader
            icon={MedNecIcon}
            title="Case Detail"
            subtitle={`${currentCase.id} — ${currentCase.status}`}
            stats={[]}
            actions={actions}
          />

          <Box sx={{ p: 3 }}>
            {transitionError && (
              <Alert severity="error" onClose={() => setTransitionError(null)} sx={{ mb: 2 }}>
                {transitionError}
              </Alert>
            )}
            <Label>Patient Demographics</Label>
            <PatientSection mode="edit" />

            <Label>Physician</Label>
            <PhysicianSection mode="edit" />

            <CaseFormTabs mode="edit" />
          </Box>

          <Dialog open={!!confirmDialog} onClose={() => setConfirmDialog(null)}>
            <DialogTitle>{confirmDialog?.label}</DialogTitle>
            <DialogContent>
              <DialogContentText>
                Are you sure you want to {confirmDialog?.label.toLowerCase()}? This action will update the case status.
              </DialogContentText>
            </DialogContent>
            <DialogActions>
              <Button onClick={() => setConfirmDialog(null)}>Cancel</Button>
              <Button color="primary" variant="contained" onClick={confirmTransition} disabled={isTransitioning}>
                {isTransitioning ? 'Processing...' : 'Confirm'}
              </Button>
            </DialogActions>
          </Dialog>
        </PageCard>
      </Box>
    </PageContainer>
  );
};

export default MedNecCaseDetail;
